import React, { useState } from "react";
import { Badge, Button, Checkbox, Input, Text } from "@fluentui/react-components";
import { AddRegular, DeleteRegular, DismissRegular } from "@fluentui/react-icons";
import { isOverdue, type FollowUpTask } from "../tasks";

interface TasksPanelProps {
  tasks: FollowUpTask[];
  canAddFromEmail: boolean;
  defaultNewTitle: string;
  /** Whether a Graph-connected account is active — gates the "Not synced"
   * badge, since local-only (EWS/unavailable) accounts have nothing to sync
   * against and shouldn't be nudged about it. */
  graphConnected: boolean;
  onAdd: (title: string) => void;
  onToggleDone: (id: string) => void;
  onUpdateTitle: (id: string, title: string) => void;
  onUpdateDueDate: (id: string, dueDate: string | undefined) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

/** Open tasks first, then done; each group newest-first by createdAt. */
function ordered(tasks: FollowUpTask[]): FollowUpTask[] {
  return [...tasks].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    return b.createdAt - a.createdAt;
  });
}

export function TasksPanel({
  tasks,
  canAddFromEmail,
  defaultNewTitle,
  graphConnected,
  onAdd,
  onToggleDone,
  onUpdateTitle,
  onUpdateDueDate,
  onDelete,
  onClose,
}: TasksPanelProps) {
  const [newTitle, setNewTitle] = useState(defaultNewTitle);

  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Text weight="semibold" size={400}>
          Tasks
        </Text>
        <Button
          appearance="subtle"
          icon={<DismissRegular />}
          aria-label="Close tasks"
          onClick={onClose}
        />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Input
          value={newTitle}
          placeholder="Task title"
          onChange={(_, d) => setNewTitle(d.value)}
          style={{ flex: 1 }}
          aria-label="New task title"
        />
        <Button
          appearance="primary"
          icon={<AddRegular />}
          disabled={!canAddFromEmail || newTitle.trim().length === 0}
          onClick={() => onAdd(newTitle.trim())}
        >
          Add task
        </Button>
      </div>

      {tasks.length === 0 ? (
        <Text size={200} style={{ opacity: 0.7 }}>
          No tasks yet.
        </Text>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, overflowY: "auto" }}>
          {ordered(tasks).map((t) => (
            <div
              key={t.id}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}
            >
              <Checkbox
                checked={t.done}
                onChange={() => onToggleDone(t.id)}
                aria-label={`Mark ${t.title} done`}
              />
              <div
                style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}
              >
                <Input
                  value={t.title}
                  onChange={(_, d) => onUpdateTitle(t.id, d.value)}
                  aria-label={`Task title ${t.title}`}
                  style={{ textDecoration: t.done ? "line-through" : "none" }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Input
                    type="date"
                    value={t.dueDate ?? ""}
                    onChange={(_, d) => onUpdateDueDate(t.id, d.value || undefined)}
                    aria-label={`Due date ${t.title}`}
                    size="small"
                  />
                  {isOverdue(t) && (
                    <Badge color="danger" appearance="tint" size="small">
                      Overdue
                    </Badge>
                  )}
                  {graphConnected && !t.graphTaskId && (
                    <Badge color="informative" appearance="tint" size="small">
                      Not synced
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                appearance="subtle"
                size="small"
                icon={<DeleteRegular />}
                aria-label={`Delete ${t.title}`}
                onClick={() => onDelete(t.id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
